import { el, clear, fmtDataHora, formatarNome, mascararCpf } from '../lib/dom.js';
import { gerarProducaoRecepcao, gerarVisitas } from '../domain/relatorios.js';
import { renderTabela } from '../ui/table.js';
import { barrasH, barrasV, barrasHempilhada, barrasVempilhada, legenda, corPaleta } from '../ui/chart.js';
import { loader } from '../ui/loader.js';
import { toast } from '../lib/toast.js';
import { abrirModal } from '../ui/modal.js';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const HORAS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));
const TIPOS_PART = [
  { chave: 'chaves', nome: 'Chaves' },
  { chave: 'devolvidas', nome: 'Devoluções' }, { chave: 'crachas', nome: 'Crachás' },
  { chave: 'visitas', nome: 'Visitas' }, { chave: 'sugestoes', nome: 'Sugestões' },
].map((t, i) => ({ ...t, cor: corPaleta(i) }));

const TIPOS_REL = {
  producao_recepcao: { rotulo: 'Produção da recepção', titulo: (mm, yy) => `Produção da recepção — ${MESES[mm]}/${yy}`, gerar: gerarProducaoRecepcao, render: renderProducao },
  visitas: { rotulo: 'Visitas administrativas', titulo: (mm, yy) => `Visitas administrativas — ${MESES[mm]}/${yy}`, gerar: gerarVisitas, render: renderVisitas },
};

const sub = (t) => el('div', { class: 'rel-sub', text: t });
function card(rotulo, valor, icone, dica) {
  const lbl = dica
    ? el('div', { class: 'rel-card__l' }, [rotulo + ' ', el('i', { class: 'ph ph-info col-info', title: dica })])
    : el('div', { class: 'rel-card__l', text: rotulo });
  return el('div', { class: 'rel-card' }, [
    el('div', { class: 'rel-card__ic' }, [el('i', { class: 'ph ' + icone })]),
    el('div', {}, [el('div', { class: 'rel-card__n', text: String(valor) }), lbl]),
  ]);
}
function cardTexto(rotulo, valor, icone) {
  return el('div', { class: 'rel-card' }, [
    el('div', { class: 'rel-card__ic' }, [el('i', { class: 'ph ' + icone })]),
    el('div', { style: 'min-width:0' }, [
      el('div', { style: 'font-size:15px; font-weight:700; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; cursor:help', title: valor, text: valor }),
      el('div', { class: 'rel-card__l', text: rotulo }),
    ]),
  ]);
}
const colNum = (chave, rotulo, dica) => ({ chave, rotulo, largura: '9%', alinhar: 'centro', dica, render: (l) => l[chave] ?? 0 });
const primeiroNome = (s) => (formatarNome(s || '').split(/\s+/)[0] || '');
function iniciais(nome) {
  const p = formatarNome(nome || '').split(/\s+/).filter(Boolean);
  return (((p[0] || '')[0] || '') + (p.length > 1 ? (p[p.length - 1][0] || '') : '')).toUpperCase() || '?';
}
const CAT_ABREV = {
  'Atendimento ao Paciente/Acompanhante': 'Paciente',
  'Reunião ou Atendimento Administrativo': 'Reunião',
  'Entrega ou Retirada de Documento': 'Documento',
  'Ensino e Atividade Acadêmica': 'Ensino',
  'Pesquisa': 'Pesquisa',
  'Serviço Técnico, Manutenção ou Insumos': 'Serviço',
  'Visita Institucional, Fiscalização ou Auditoria': 'Inst./Fisc.',
  'Ouvidoria': 'Ouvidoria',
  'Outros': 'Outros',
  '(não informado)': 'N/I',
};
const abrevCat = (m) => CAT_ABREV[m] || m;
function abrirDetalheCelula(setor, motivo, det) {
  const itens = (det || []).map((x) => (typeof x === 'string' ? { t: x, n: null } : x)).filter((x) => x && x.t);
  itens.sort((a, b) => (b.n || 0) - (a.n || 0));
  const lista = el('div', { class: 'rel-det-lista' }, itens.length ? itens.map((x) => el('div', { class: 'rel-det-item' }, [
    el('span', { class: 'rel-det-txt', text: x.t }),
    x.n != null ? el('span', { class: 'rel-det-n', text: x.n + '×' }) : null,
  ])) : [el('p', { class: 'view__sub', text: 'Sem detalhamento informado.' })]);
  abrirModal({
    titulo: 'Detalhes das visitas',
    corpo: [el('p', { class: 'view__sub', style: 'margin-top:0', text: `${setor} · ${motivo}` }), lista],
    acoes: [{ rotulo: 'Fechar', classe: 'btn btn--primario' }],
  });
}

function renderProducao(titulo, d, _periodoLabel, opts) {
  const box = el('div', { class: 'painel', style: 'margin-bottom:16px' });
  box.append(el('h2', { class: 'rel-tit', text: titulo }));
  const r = d.resumo || {};
  box.append(el('div', { class: 'rel-cards' }, [
    card('Colaboradores sugeridos / aprovados', `${r.colab_sugeridos || 0} / ${r.colab_aprovados || 0}`, 'ph-user-plus', 'Sugeridos = pessoas marcadas como "Colaborador não cadastrado" na entrega avulsa (nomes distintos no período). Aprovados = quantas dessas já constam como colaborador cadastrado (por nome).'),
    card('Setores sugeridos / aprovados', `${r.setor_sugeridos || 0} / ${r.setor_aprovados || 0}`, 'ph-tree-structure', 'Sugeridos = sugestões de setor feitas no período (pela recepção ou via "setor não listado" na visita). Aprovados = quantas o gestor aceitou (virou setor novo, ou foi fundida num setor já existente).'),
    card('Chaves sugeridas / aprovadas', `${r.chave_sugeridas || 0} / ${r.chave_aprovadas || 0}`, 'ph-key', 'Sugeridas = sugestões de chave (criar ou cancelar) feitas no período. Aprovadas = quantas o gestor aprovou.'),
    card('Motivos/empresas sugeridos / aprovados', `${r.me_sugeridos || 0} / ${r.me_aprovados || 0}`, 'ph-building', 'Sugeridos = motivos e empresas/órgãos novos sugeridos pela recepção no período. Aprovados = quantos o gestor aprovou. (Recusados são removidos, então não entram na conta.)'),
  ]));
  const us = d.usuarios || [];
  us.forEach((u) => { u.chaves = Number(u.ent_cad || 0) + Number(u.ent_avulso || 0); u.sugestoes = Number(u.sug_ativas || 0) + Number(u.sug_passivas || 0); });
  const temNovo = us.length > 0 && us[0].ent_cad !== undefined;
  if (!temNovo && us.length) box.append(el('div', { class: 'rel-aviso', text: 'Este relatório foi gerado com a versão anterior do cálculo. Rode a migração 0049 (atualizada) e clique em Gerar novamente para ver entregas por cadastro/avulso e os gráficos por horário.' }));
  box.append(sub(`Por recepcionista (${us.length})`));
  if (!us.length) { box.append(el('p', { class: 'view__sub', text: 'Nenhum recepcionista cadastrado (verifique o perfil dos usuários).' })); return box; }
  const tb = el('div'); box.append(tb);
  renderTabela(tb, {
    colunas: [
      { chave: 'nome', rotulo: 'Recepcionista', truncar: true, dica: 'Quem operou o balcão (perfil de recepção). Supervisor e gestor não entram nesta contagem.', render: (l) => formatarNome(l.nome) },
      colNum('chaves', 'Chaves', 'Chaves entregues no período (a pessoas cadastradas + avulsas).'),
      colNum('devolvidas', 'Devoluções', 'Devoluções de chaves registradas pela pessoa.'),
      colNum('crachas', 'Crachás', 'Crachás esquecidos registrados.'),
      colNum('visitas', 'Visitas', 'Visitas administrativas atendidas.'),
      colNum('sugestoes', 'Sugestões', 'Sugestões feitas por esta pessoa (de chave, de setor e de melhoria do sistema).'),
      colNum('total', 'Total', 'Soma de todos os registros do período (todas as colunas anteriores).'),
    ], linhas: us, buscarEm: ['nome'], porPagina: opts?.porPagina ?? 30,
  });
  const assuntos = d.assuntos || [];
  if (assuntos.length) {
    const temRecep = assuntos.some((a) => a.recep);
    if (temRecep) {
      box.append(sub('Entregas de chaves avulsas: assunto × recepcionista'));
      const receps = [...new Set(assuntos.map((a) => a.recep).filter(Boolean))].sort((a, b) => primeiroNome(a).localeCompare(primeiroNome(b)));
      const keysTotal = {};
      us.forEach((u) => { keysTotal[u.nome] = Number(u.ent_cad || 0) + Number(u.ent_avulso || 0); });
      const mapa = {};
      assuntos.forEach((a) => { const k = a.motivo + '||' + a.recep; mapa[k] = (mapa[k] || 0) + Number(a.n || 0); });
      const linhas = [...new Set(assuntos.map((a) => a.motivo))].map((mt) => {
        const row = { motivo: mt, _total: 0 };
        receps.forEach((rf) => { const v = mapa[mt + '||' + rf] || 0; row['r_' + rf] = v; row._total += v; });
        return row;
      }).sort((a, b) => b._total - a._total);
      const colunas = [{ chave: 'motivo', rotulo: 'Assunto', largura: '26%', truncar: true, dica: 'Motivo da entrega de chave avulsa (sem cadastro). Em cada coluna: nº de chaves avulsas e, entre parênteses, o % sobre o TOTAL de chaves que aquele recepcionista entregou no período (cadastradas + avulsas). Ex.: 4 avulsas de 47 entregues = 9%.' }];
      receps.forEach((rf) => colunas.push({ chave: 'r_' + rf, rotulo: primeiroNome(rf), alinhar: 'centro', dica: formatarNome(rf) + ': chaves avulsas por assunto; o % é sobre o total de chaves entregues por ele (cadastradas + avulsas).', render: (l) => { const v = l['r_' + rf]; if (!v) return ''; const tt = keysTotal[rf] || 0; return tt ? `${v} (${Math.round((v / tt) * 100)}%)` : String(v); } }));
      colunas.push({ chave: '_total', rotulo: 'Total', alinhar: 'centro' });
      const ta = el('div'); box.append(ta);
      renderTabela(ta, { colunas, linhas, buscarEm: ['motivo'], porPagina: opts?.porPagina ?? 15 });
    } else {
      box.append(sub('Entregas avulsas por assunto (motivo)'));
      const ta = el('div'); box.append(ta);
      renderTabela(ta, { colunas: [
        { chave: 'motivo', rotulo: 'Assunto', truncar: true, dica: 'Motivo informado na entrega avulsa (sem cadastro).' },
        { chave: 'n', rotulo: 'Qtd.', largura: '90px', alinhar: 'centro', dica: 'Quantas entregas avulsas com esse assunto.' },
      ], linhas: assuntos, buscarEm: ['motivo'], porPagina: opts?.porPagina ?? 10 });
    }
  }
  box.append(sub('Participação por recepcionista'));
  if (temNovo) {
    box.append(legenda(TIPOS_PART.map((t) => ({ nome: t.nome, cor: t.cor }))));
    box.append(barrasHempilhada(us, TIPOS_PART, (u) => formatarNome(u.nome)));
  } else {
    box.append(barrasH(us.map((u) => ({ rotulo: formatarNome(u.nome), valor: u.total || 0 }))));
  }
  const hu = d.horas_usuario || [];
  const diasU = d.dias_usuario || {};
  if (hu.length) {
    const cores = {}; us.forEach((u, i) => { cores[u.nome] = corPaleta(i); });
    const series = us.filter((u) => (hu.find((x) => x.nome === u.nome))).map((u) => { const hm = (hu.find((x) => x.nome === u.nome) || {}).horas || {}; const dd = Number(diasU[u.nome] || 0); return { nome: formatarNome(u.nome), cor: cores[u.nome], valores: HORAS.map((_, h) => { const tot = hm[String(h)] || 0; return dd ? Math.round((tot / dd) * 10) / 10 : 0; }) }; });
    if (series.some((s) => s.valores.some((v) => v > 0))) {
      box.append(sub('Média de registros por hora do dia — por recepcionista (por dia trabalhado)'));
      box.append(legenda(series.map((s) => ({ nome: s.nome, cor: s.cor }))));
      box.append(barrasVempilhada(HORAS, series));
    }
  } else if (d.horas && Object.keys(d.horas).length) {
    box.append(sub('Registros por hora do dia'));
    box.append(barrasV(HORAS.map((l) => ({ rotulo: l, valor: d.horas[String(+l)] || 0 }))));
  }
  return box;
}

/** Panorama de visitas administrativas — acompanhamento (descritivo, para decisao). */
function renderVisitas(titulo, d, periodoLabel, opts) {
  const box = el('div', { class: 'painel', style: 'margin-bottom:16px' });
  box.append(el('h2', { class: 'rel-tit', text: titulo }));
  const fora = (lista) => (lista || []).filter((x) => x.rotulo !== '(não informado)');
  const setores = fora(d.por_setor), motivos = fora(d.por_motivo), empresas = fora(d.por_empresa);
  const horas = d.horas || {};
  let horaPico = '—', hpMax = -1;
  Object.keys(horas).forEach((h) => { if (horas[h] > hpMax) { hpMax = horas[h]; horaPico = `${h}h`; } });

  box.append(el('div', { class: 'rel-cards' }, [
    card('Visitas no período', d.total || 0, 'ph-user-square'),
    card('Visitantes diferentes', d.visitantes || 0, 'ph-identification-card'),
    cardTexto('Setor mais procurado', (setores[0] && setores[0].rotulo) || '—', 'ph-tree-structure'),
    cardTexto('Horário de pico', horaPico, 'ph-clock'),
  ]));

  const rec = d.recorrentes || [];
  box.append(sub('Quem mais vem'));
  if (!rec.length) box.append(el('p', { class: 'view__sub', text: 'Ninguém repetiu visita no período.' }));
  else {
    box.append(el('p', { class: 'view__sub', style: 'margin:-2px 0 8px', text: `Quantas vezes cada pessoa veio no período selecionado${periodoLabel ? ' (' + periodoLabel + ')' : ''}.` }));
    const lista = el('div', { class: 'rel-quem' });
    rec.slice(0, 8).forEach((p) => {
      const proc = p.setor || p.motivo || '';
      const subTxt = proc ? 'procura: ' + proc : 'visitante recorrente';
      lista.append(el('div', { class: 'rel-quem__row' }, [
        el('div', { class: 'rel-quem__av', text: iniciais(p.nome) }),
        el('div', { class: 'rel-quem__txt' }, [
          el('div', { class: 'rel-quem__nome', text: formatarNome(p.nome), title: formatarNome(p.nome) + (p.cpf ? ' · ' + mascararCpf(p.cpf) : '') }),
          el('div', { class: 'rel-quem__sub', text: subTxt, title: subTxt }),
        ]),
        el('div', { class: 'rel-quem__n', text: `${p.n} visita${p.n > 1 ? 's' : ''}` }),
      ]));
    });
    box.append(lista);
  }

  const duo = el('div', { class: 'rel-duo' });
  const c1 = el('div'); c1.append(sub('Onde vão (setores)'));
  c1.append(setores.length ? barrasH(setores.slice(0, 8).map((x) => ({ rotulo: x.rotulo, valor: x.n }))) : el('p', { class: 'view__sub', text: 'Sem dados no período.' }));
  const c2 = el('div'); c2.append(sub('O que buscam (motivo)'));
  c2.append(motivos.length ? barrasH(motivos.slice(0, 8).map((x) => ({ rotulo: x.rotulo, valor: x.n }))) : el('p', { class: 'view__sub', text: 'Sem dados no período.' }));
  duo.append(c1, c2); box.append(duo);

  const sm = d.setor_motivo || [];
  if (sm.length) {
    box.append(sub('Onde vão × o que buscam'));
    const cats = [...new Set(sm.map((x) => x.motivo))];
    const tot = {}; sm.forEach((x) => { tot[x.motivo] = (tot[x.motivo] || 0) + Number(x.n || 0); });
    cats.sort((a, b) => (tot[b] || 0) - (tot[a] || 0));
    const mapa = {}, detMapa = {}; sm.forEach((x) => { const k = x.setor + '||' + x.motivo; mapa[k] = (mapa[k] || 0) + Number(x.n || 0); if (x.detalhes && x.detalhes.length) detMapa[k] = x.detalhes; });
    const linhas = [...new Set(sm.map((x) => x.setor))].map((st) => {
      const row = { setor: st, _total: 0 };
      cats.forEach((c) => { const val = mapa[st + '||' + c] || 0; row['c_' + c] = val; row._total += val; });
      return row;
    }).sort((a, b) => b._total - a._total);
    const colunas = [{ chave: 'setor', rotulo: 'Setor', largura: '26%', truncar: true, dica: 'Setor procurado. Cada coluna é um motivo; o número é quantas visitas daquele setor por aquele motivo. Clique no número (pontilhado) para ver os detalhes agrupados por frequência.' }];
    cats.forEach((c) => colunas.push({ chave: 'c_' + c, rotulo: abrevCat(c), alinhar: 'centro', dica: c, render: (l) => {
      const v = l['c_' + c]; if (!v) return '';
      const det = detMapa[l.setor + '||' + c] || [];
      if (!det.length) return String(v);
      return el('span', { class: 'cel-det', title: 'Clique para ver os detalhes', onClick: () => abrirDetalheCelula(l.setor, c, det) }, String(v));
    } }));
    colunas.push({ chave: '_total', rotulo: 'Total', alinhar: 'centro' });
    const tcz = el('div'); box.append(tcz);
    renderTabela(tcz, { colunas, linhas, buscarEm: ['setor'], porPagina: opts?.porPagina ?? 12 });
  }

  if (empresas.length) {
    box.append(sub('Empresas/órgãos que mais aparecem'));
    box.append(barrasH(empresas.slice(0, 8).map((x) => ({ rotulo: x.rotulo, valor: x.n }))));
  }

  if (Object.keys(horas).length) {
    box.append(sub('Quando costumam vir'));
    box.append(barrasV(HORAS.map((l) => ({ rotulo: l, valor: horas[String(+l)] || 0 }))));
  }
  return box;
}

export const paginaRelatorios = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Relatórios' }),
      el('p', { class: 'view__sub', text: 'Sempre atualizados — escolha o tipo e o período. Não precisa gerar nem salvar.' }),
    );

    const pad = (n) => String(n).padStart(2, '0');
    const fmtDia = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    const hoje = new Date();
    const isoDia = `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}-${pad(hoje.getDate())}`;
    const isoMes = `${hoje.getFullYear()}-${pad(hoje.getMonth() + 1)}`;
    const anoAtual = hoje.getFullYear();
    const isoWeekStr = (date) => {
      const t = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dow = (t.getDay() + 6) % 7; t.setDate(t.getDate() - dow + 3);
      const yy = t.getFullYear(); const ft = new Date(yy, 0, 4); const ftd = (ft.getDay() + 6) % 7; ft.setDate(ft.getDate() - ftd + 3);
      return `${yy}-W${pad(1 + Math.round((t - ft) / (7 * 86400000)))}`;
    };
    const segundaSemanaISO = (yy, w) => { const ft = new Date(yy, 0, 4); const ftd = (ft.getDay() + 6) % 7; const seg1 = new Date(yy, 0, 4 - ftd); return new Date(seg1.getFullYear(), seg1.getMonth(), seg1.getDate() + (w - 1) * 7); };
    const estado = { tipo: Object.keys(TIPOS_REL)[0], gran: 'dia' };

    const abas = el('div', { class: 'abas' });
    const botoesAba = {};
    Object.entries(TIPOS_REL).forEach(([k, v]) => {
      const b = el('button', { class: 'aba' + (k === estado.tipo ? ' aba--ativa' : ''), onClick: () => { estado.tipo = k; Object.entries(botoesAba).forEach(([kk, bb]) => bb.classList.toggle('aba--ativa', kk === k)); atualizar(); } }, v.rotulo);
      botoesAba[k] = b; abas.append(b);
    });
    container.append(abas);

    const granSel = el('select', {}, [
      el('option', { value: 'dia' }, 'Dia'),
      el('option', { value: 'semana' }, 'Semana'),
      el('option', { value: 'mes' }, 'Mês'),
      el('option', { value: 'ano' }, 'Ano'),
    ]);
    granSel.value = estado.gran;
    const inputDia = el('input', { type: 'date', value: isoDia, max: isoDia });
    const inputSemana = el('input', { type: 'week', value: isoWeekStr(hoje), max: isoWeekStr(hoje) });
    const inputMes = el('input', { type: 'month', value: isoMes, max: isoMes });
    const anosOpc = []; for (let a = anoAtual; a >= anoAtual - 6; a--) anosOpc.push(el('option', { value: String(a) }, String(a)));
    const inputAno = el('select', {}, anosOpc);
    [inputDia, inputSemana, inputMes, inputAno].forEach((i) => i.addEventListener('change', () => atualizar()));

    const valWrap = el('div', { class: 'campo', style: 'margin:0' });
    function montarValInput() {
      clear(valWrap);
      const lbl = { dia: 'Dia', semana: 'Semana', mes: 'Mês', ano: 'Ano' }[estado.gran];
      const inp = { dia: inputDia, semana: inputSemana, mes: inputMes, ano: inputAno }[estado.gran];
      valWrap.append(el('label', { text: lbl }), inp);
    }
    granSel.addEventListener('change', () => { estado.gran = granSel.value; montarValInput(); atualizar(); });
    montarValInput();
    container.append(el('div', { class: 'painel', style: 'margin:12px 0 16px;display:flex;gap:16px;align-items:flex-end;flex-wrap:wrap' }, [
      el('div', { class: 'campo', style: 'margin:0' }, [el('label', { text: 'Período' }), granSel]),
      valWrap,
      el('button', { class: 'btn', style: 'margin-left:auto', title: 'Abre a janela de impressão — escolha "Salvar como PDF" (ou imprima no papel)', onClick: imprimirRelatorio }, [el('i', { class: 'ph ph-printer' }), ' Gerar PDF']),
    ]));

    function intervalo() {
      if (estado.gran === 'dia') {
        const [y, m, dd] = inputDia.value.split('-').map(Number);
        return { de: new Date(y, m - 1, dd, 0, 0, 0), ate: new Date(y, m - 1, dd + 1, 0, 0, 0), titulo: 'Dia ' + fmtDia(new Date(y, m - 1, dd)) };
      }
      if (estado.gran === 'semana') {
        const [y, w] = (inputSemana.value || '').split('-W').map(Number);
        const de = segundaSemanaISO(y, w); de.setHours(0, 0, 0, 0);
        const ate = new Date(de.getFullYear(), de.getMonth(), de.getDate() + 7, 0, 0, 0);
        const fim = new Date(de.getFullYear(), de.getMonth(), de.getDate() + 6);
        return { de, ate, titulo: 'Semana ' + fmtDia(de) + ' a ' + fmtDia(fim) };
      }
      if (estado.gran === 'mes') {
        const [y, m] = inputMes.value.split('-').map(Number);
        return { de: new Date(y, m - 1, 1, 0, 0, 0), ate: new Date(y, m, 1, 0, 0, 0), titulo: MESES[m - 1] + '/' + y };
      }
      const y = Number(inputAno.value);
      return { de: new Date(y, 0, 1, 0, 0, 0), ate: new Date(y + 1, 0, 1, 0, 0, 0), titulo: 'Ano ' + y };
    }

    const resultado = el('div');
    container.append(resultado);
    let seq = 0;
    let ultimo = null; // ultimo relatorio carregado (p/ gerar PDF)
    async function atualizar() {
      const def = TIPOS_REL[estado.tipo];
      const per = intervalo();
      if (!per.de || isNaN(per.de.getTime())) { clear(resultado); resultado.append(el('p', { class: 'view__sub', text: 'Escolha um período válido.' })); return; }
      const meu = ++seq;
      clear(resultado); resultado.append(loader('Carregando…'));
      const dados = await def.gerar(per.de.toISOString(), per.ate.toISOString());
      if (meu !== seq) return;
      clear(resultado);
      if (!dados) { ultimo = null; resultado.append(el('p', { class: 'view__sub', text: 'Não foi possível carregar (verifique as migrações).' })); return; }
      ultimo = { def, dados, per };
      resultado.append(def.render(`${def.rotulo} — ${per.titulo}`, dados, per.titulo));
    }
    function imprimirRelatorio() {
      if (!ultimo || !ultimo.dados) { toast('Abra um relatório primeiro.', 'info'); return; }
      const { def, dados, per } = ultimo;
      const titulo = `${def.rotulo} — ${per.titulo}`;
      const cab = el('div', { class: 'print-cab' }, [
        el('img', { class: 'print-cab__logo', src: '/logo.png', alt: '' }),
        el('span', { class: 'print-cab__txt', text: 'AcessaHUOL · relatório gerado em ' + new Date().toLocaleString('pt-BR') }),
      ]);
      // re-renderiza o relatorio com TODAS as linhas (porPagina alto) numa area so de impressao
      const area = el('div', { class: 'print-area' }, [cab, def.render(titulo, dados, per.titulo, { porPagina: 1e9 })]);
      document.body.append(area);
      const limpar = () => { if (area.parentNode) area.remove(); window.removeEventListener('afterprint', limpar); };
      window.addEventListener('afterprint', limpar);
      window.print();
      setTimeout(limpar, 120000); // fallback se afterprint nao disparar
    }
    atualizar();
  },
};